import supabase from '@/lib/supabase';
import { ElementType, RawChapter, RawLesson, RawElement, RawQuest, ChapterSchema, LessonSchema, ElementSchema, QuestSchema, Quest } from '@/constant/schema';
import { preloadQuestionsToCache } from '@/lib/practiceHelper';

const fetchQuestLastUpdateTime = async (): Promise<string> => {
  const { data, error } = await supabase
    .from('syllabus_quest')
    .select('created_at')
    .order('created_at', { ascending: false })
    .limit(1);
  if (error) {
    console.error('Error fetching quest last update time:', error);
    throw error;
  }
  return data[0].created_at;
}

const fetchChapterLastUpdateTime = async (): Promise<string> => {
  const { data, error } = await supabase
    .from('syllabus_chapter')
    .select('created_at')
    .order('created_at', { ascending: false })
    .limit(1);
  if (error) {
    console.error('Error fetching chapter last update time:', error);
    throw error;
  }
  return data[0].created_at;
}

const fetchLessonLastUpdateTime = async (): Promise<string> => {
  const { data, error } = await supabase
    .from('syllabus_lesson')
    .select('created_at')
    .order('created_at', { ascending: false })
    .limit(1);
  if (error) {
    console.error('Error fetching lesson last update time:', error);
    throw error;
  }
  return data[0].created_at;
}

const fetchElementLastUpdateTime = async (): Promise<string> => {
  const { data, error } = await supabase
    .from('syllabus_element')
    .select('created_at')
    .order('created_at', { ascending: false })
    .limit(1);
  if (error) {
    console.error('Error fetching element last update time:', error);
    throw error;
  }
  return data[0].created_at;
}

const getLatestUpdateTime = async () => {
  const times = await Promise.all([
    fetchQuestLastUpdateTime(),
    fetchChapterLastUpdateTime(),
    fetchLessonLastUpdateTime(),
    fetchElementLastUpdateTime()
  ]);
  
  return Math.max(...times.map(t => new Date(t).getTime()));
};

export const fetchChapters = async (questId: string): Promise<RawChapter[]> => {
  const { data, error } = await supabase
    .from('syllabus_chapter')
    .select('*')
    .eq('quest_id', questId)
    .order('order');

  if (error) {
    console.error('Error fetching chapters:', error);
    throw error;
  }
  return ChapterSchema.array().parse(data);
}

export const fetchLessonsByChapterIdList = async (chapterIdList: string[]): Promise<RawLesson[]> => {
  const { data, error } = await supabase
    .from('syllabus_lesson')
    .select('*')
    .in('chapter_id', chapterIdList)
    .order('order');

  if (error) {
    console.error('Error fetching lessons:', error);
    throw error;
  }
  return LessonSchema.array().parse(data);
}

export const fetchElements = async (lessonId: string): Promise<RawElement[]> => {
  // Get all valid element types
  const validElementTypes = Object.values(ElementType);

  const { data, error } = await supabase
    .from('syllabus_element')
    .select('*')
    .eq('lesson_id', lessonId)
    .in('element_type', validElementTypes)
    .order('order');

  if (error) {
    console.error('Error fetching elements:', error);
    throw error;
  }
  return ElementSchema.array().parse(data);
}

/**
 * Fetch elements from syllabus_skill table using skill IDs
 * Only returns question elements with review_status='pass'
 * @param skillIds Array of skill IDs from lesson.skill_list
 * @returns Array of elements concatenated from all skills in order, filtered by review_status
 */
export const fetchElementsFromSkills = async (skillIds: string[]): Promise<RawElement[]> => {
  if (!skillIds || skillIds.length === 0) {
    return [];
  }

  // Batch query all skills
  const { data: skills, error } = await supabase
    .from('syllabus_skill')
    .select('element_id, elements_json')
    .in('element_id', skillIds);

  if (error) {
    console.error('Error fetching skills:', error);
    throw error;
  }

  if (!skills || skills.length === 0) {
    console.warn(`No skills found for IDs: ${skillIds.join(', ')}`);
    return [];
  }

  // Create a map for quick lookup
  const skillsMap = new Map<string, any>();
  skills.forEach(skill => {
    skillsMap.set(skill.element_id, skill);
  });

  // Step 1: Parse all elements and collect question IDs
  const parsedSkills = new Map<string, any[]>();
  const questionIdSet = new Set<string>();

  for (const skillId of skillIds) {
    const skill = skillsMap.get(skillId);
    if (!skill) {
      console.warn(`Skill ${skillId} not found in database`);
      continue;
    }

    // Parse elements_json - handle both array and string formats
    try {
      let elementsJson = skill.elements_json;
      let parsedElements: any[] = [];

      if (Array.isArray(elementsJson)) {
        parsedElements = elementsJson;
      } else if (typeof elementsJson === 'string') {
        const parsed = JSON.parse(elementsJson);
        if (Array.isArray(parsed)) {
          parsedElements = parsed;
        }
      } else if (elementsJson) {
        // If it's an object, wrap it in an array
        parsedElements = [elementsJson];
      }

      parsedSkills.set(skillId, parsedElements);

      // Collect question_v2 IDs for review_status check (question type is self-contained)
      parsedElements.forEach((element: any) => {
        if (element.element_type === 'question_v2' && element.question_id) {
          questionIdSet.add(element.question_id);
        }
      });
    } catch (error) {
      console.error(`Failed to parse elements_json for skill ${skillId}:`, error);
      parsedSkills.set(skillId, []);
    }
  }

  // Step 2: Batch-fetch full question data (select('*') instead of just 'id, review_status')
  // Full data is cached so components skip individual DB calls (eliminates N+1 queries)
  let passQuestionIds = new Set<string>();
  if (questionIdSet.size > 0) {
    const { data: questions, error: questionsError } = await supabase
      .from('questions')
      .select('*')
      .in('id', Array.from(questionIdSet));

    if (questionsError) {
      console.error('Error fetching question data:', questionsError);
    } else if (questions) {
      passQuestionIds = new Set(
        questions.filter(q => q.review_status === 'pass').map(q => q.id)
      );
      // Pre-populate cache so getQuestionById() returns instantly for these IDs
      preloadQuestionsToCache(questions);

      // Log filtering statistics in development
      if (process.env.NODE_ENV === 'development') {
        const filteredCount = questionIdSet.size - passQuestionIds.size;
        if (filteredCount > 0) {
          console.log(
            `[fetchElementsFromSkills] Filtered out ${filteredCount} non-pass questions (${passQuestionIds.size} pass, ${questionIdSet.size} total)`
          );
        }
      }
    }
  }

  // Step 3: Filter elements and concatenate in order
  const allElements: RawElement[] = [];
  for (const skillId of skillIds) {
    const elements = parsedSkills.get(skillId) || [];

    const filteredElements = elements.filter((element: any) => {
      // question_v2 references the questions table — only include if review_status='pass'
      if (element.element_type === 'question_v2') {
        return element.question_id && passQuestionIds.has(element.question_id);
      }
      // question has embedded content and does not depend on the questions table
      // All other element types are kept as-is
      return true;
    });

    allElements.push(
      ...filteredElements.map((element: any) => ({
        ...element,
        skill_id: skillId,
      })),
    );
  }

  return allElements;
}

/**
 * Get elements for a lesson - handles both dynamic and static lessons
 * This is the unified function for retrieving lesson elements
 * @param lesson The lesson object
 * @returns Array of elements (no filtering applied)
 */
export const getLessonElements = async (lesson: RawLesson): Promise<RawElement[]> => {
  let elements: RawElement[] = [];

  // For dynamic lessons, must use skill_list
  if (lesson?.type === 'dynamic') {
    if (!lesson.skill_list || lesson.skill_list.length === 0) {
      throw new Error(`Dynamic lesson ${lesson.id} missing skill_list`);
    }

    // Use lesson.elements directly — already fetched by fetchLessonById with select('*')
    const syllabusElements: RawElement[] =
      (lesson.elements && Array.isArray(lesson.elements) && lesson.elements.length > 0)
        ? lesson.elements
        : [];

    // Separate recommend_videos from other elements
    const recommendVideos = syllabusElements.filter(el => el.element_type === 'recommend_videos');
    const otherElements = syllabusElements.filter(el => el.element_type !== 'recommend_videos');

    // Fetch skill elements
    const skillElements = await fetchElementsFromSkills(lesson.skill_list);

    // Combine in new order: recommend_videos → skill elements → other elements
    elements = [...recommendVideos, ...skillElements, ...otherElements];
  } else {
    // For static lessons or when type is not specified, fetch from syllabus_element table
    elements = await fetchElements(lesson.id);

    // Append lesson.elements (if any) to the end for static lessons
    // Already fetched by fetchLessonById with select('*')
    if (lesson.elements && Array.isArray(lesson.elements) && lesson.elements.length > 0) {
      elements.push(...lesson.elements);
    }
  }

  return elements;
}

export const fetchQuestById = async (id: string): Promise<RawQuest> => {
  const { data, error } = await supabase
    .from('syllabus_quest')
    .select('*')
    .eq('id', id)
    .single();

  if (error) {
    console.error('Error fetching quest by id:', error);
    throw error;
  }
  return QuestSchema.parse(data);
}

/**
 * Fetch all quests with optional subject filtering
 * Only returns visible and unlocked quests
 * @param subject Optional subject to filter by
 * @returns Array of quests
 */
export const fetchAllQuests = async (subject?: string): Promise<RawQuest[]> => {
  let query = supabase
    .from('syllabus_quest')
    .select('*')
    .eq('visible', true)
    .eq('lock', false)
    .order('order');

  // Apply subject filter if provided
  if (subject) {
    query = query.eq('subject', subject);
  }

  const { data, error } = await query;

  if (error) {
    console.error('Error fetching quests:', error);
    throw error;
  }

  return QuestSchema.array().parse(data || []);
}

export const fetchChapterById = async (chapterId: string): Promise<RawChapter> => {
  const { data, error } = await supabase
    .from('syllabus_chapter')
    .select('*')
    .eq('id', chapterId)
    .single();

  if (error) {
    console.error('Error fetching chapter by id:', error);
    throw error;
  }
  return ChapterSchema.parse(data);
}

export const fetchLessonById = async (lessonId: string): Promise<RawLesson | null> => {
  if (!lessonId || lessonId.trim() === '') {
    return null;
  }

  const { data, error } = await supabase
    .from('syllabus_lesson')
    .select('*')
    .eq('id', lessonId)
    .single();

  if (error) {
    console.error('Error fetching lesson by id:', error);
    throw error;
  }
  return LessonSchema.parse(data);
}

/**
 * Get question data directly by ID - more efficient method
 * @param questionIds Array of question IDs
 * @param questionType Optional question type filter
 * @returns Array of question data objects
 */
export const fetchQuestionsByIds = async (questionIds: string[], questionType?: string): Promise<any[]> => {
  if (!questionIds || questionIds.length === 0) {
    return [];
  }

  // Use JSONB query directly, this is a more efficient method
  // Note: This uses PostgreSQL-specific JSONB query functionality
  // Reference: https://supabase.com/docs/guides/database/json
  
  // Find questions containing these question IDs from syllabus_chapter table
  const chapterQuery = supabase.rpc('find_questions_in_chapters', {
    search_ids: questionIds
  });
  
  // Find questions containing these question IDs from syllabus_lesson table
  const lessonQuery = supabase.rpc('find_questions_in_lessons', {
    search_ids: questionIds
  });
  
  try {
    // Execute both queries in parallel
    const [chapterResult, lessonResult] = await Promise.all([
      chapterQuery,
      lessonQuery
    ]);
    
    if (chapterResult.error) {
      console.error('Error in chapter questions query:', chapterResult.error);
      // If RPC is not available, fall back to the old implementation
      return fetchQuestionsByIdsLegacy(questionIds, questionType);
    }
    
    if (lessonResult.error) {
      console.error('Error in lesson questions query:', lessonResult.error);
      // If RPC is not available, fall back to the old implementation
      return fetchQuestionsByIdsLegacy(questionIds, questionType);
    }
    
    // Merge results
    let allQuestions: any[] = [
      ...(chapterResult.data || []),
      ...(lessonResult.data || [])
    ];
    
    // Sort results according to the provided ID order
    if (allQuestions.length > 0) {
      const idToIndexMap: Record<string, number> = {};
      questionIds.forEach((id, index) => {
        idToIndexMap[id] = index;
      });
      
      allQuestions.sort((a, b) => {
        const indexA = idToIndexMap[a.id] ?? Number.MAX_SAFE_INTEGER;
        const indexB = idToIndexMap[b.id] ?? Number.MAX_SAFE_INTEGER;
        return indexA - indexB;
      });
    }
    
    // Filter if question type is specified
    if (questionType) {
      allQuestions = allQuestions.filter(
        q => q.question_type === questionType || q.type === questionType
      );
    }
    
    return allQuestions;
  } catch (error) {
    console.error('Error fetching questions by IDs:', error);
    // If error occurs, fall back to the old implementation
    return fetchQuestionsByIdsLegacy(questionIds, questionType);
  }
}

/**
 * Old implementation for getting questions by ID - as a backup method
 */
const fetchQuestionsByIdsLegacy = async (questionIds: string[], questionType?: string): Promise<any[]> => {
  // Find questions arrays containing these question IDs from syllabus_chapter table
  const { data: chapterData, error: chapterError } = await supabase
    .from('syllabus_chapter')
    .select('questions')
    .not('questions', 'is', null);
    
  if (chapterError) {
    console.error('Error fetching questions from chapters:', chapterError);
    throw chapterError;
  }

  // Find questions arrays containing these question IDs from syllabus_lesson table
  const { data: lessonData, error: lessonError } = await supabase
    .from('syllabus_lesson')
    .select('questions')
    .not('questions', 'is', null);

  if (lessonError) {
    console.error('Error fetching questions from lessons:', lessonError);
    throw lessonError;
  }

  // Collect all questions
  let allQuestions: any[] = [];
  
  // Extract questions from chapter data
  chapterData?.forEach(chapter => {
    if (chapter.questions && Array.isArray(chapter.questions)) {
      allQuestions.push(...chapter.questions);
    }
  });

  // Extract questions from course data
  lessonData?.forEach(lesson => {
    if (lesson.questions && Array.isArray(lesson.questions)) {
      allQuestions.push(...lesson.questions);
    }
  });

  // Filter questions by ID
  const filteredQuestions = allQuestions.filter(q => 
    questionIds.includes(q.id) && 
    (!questionType || q.question_type === questionType || q.type === questionType)
  );

  return filteredQuestions;
}

/**
 * Optimized version of getQuestById using JOIN queries to reduce database calls
 * This function combines quest, chapter, lesson, and elements data in a single query
 */
export const getQuestById = async (questId: string, chapterId: string, lessonId: string): Promise<any> => {
  try {
    // Check if lessonId is empty or invalid
    if (!lessonId || lessonId === 'empty' || lessonId === '') {
      // Only fetch quest and chapter data
      const [questData, chapterData] = await Promise.all([
        fetchQuestById(questId),
        fetchChapterById(chapterId)
      ]);

      return {
        ...questData,
        chapters: [{
          ...chapterData,
          lesson_list: []
        }],
      };
    }

    // Fetch quest, chapter, and lesson data first
    const [questData, chapterData, lessonData] = await Promise.all([
      fetchQuestById(questId),
      fetchChapterById(chapterId),
      fetchLessonById(lessonId)
    ]);

    // Check if lesson data exists
    if (!lessonData) {
      throw new Error(`Lesson with ID ${lessonId} not found`);
    }

    // Use unified function to get elements (handles both dynamic and static lessons)
    const elementsData = await getLessonElements(lessonData);

    return {
      ...questData,
      chapters: [{
        ...chapterData,
        lesson_list: [{
          ...lessonData,
          elements: elementsData
        }]
      }],
    };
  } catch (error) {
    console.error('Error in getQuestById:', error);
    throw error;
  }
}

export const getHeaderData = async (questId: string): Promise<any> => {
  try {
    // Get Quest data
    const questData = await fetchQuestById(questId);
    
    // Get Chapters data
    const chaptersData = await fetchChapters(questData.id);
    
    // Get chapter ID list
    const chapterIds = chaptersData.map(chapter => chapter.id);
    
    const lessons = await fetchLessonsByChapterIdList(chapterIds);
    
    // Group lessons by chapter_id
    const lessonsByChapterId = lessons.reduce((acc, lesson) => {
      acc[lesson.chapter_id] = [...(acc[lesson.chapter_id] || []), lesson];
      return acc;
    }, {} as Record<string, RawLesson[]>);
    
    // Get all lesson IDs
    const lessonIds = lessons.map(lesson => lesson.id);
    
    // Build chapter data, including courses and elements
    const chaptersWithLessons = chaptersData.map(chapter => {
      const chapterLessons = lessonsByChapterId[chapter.id] || [];
      
      // Iterate through lessons, aggregate data as [{lesson, elements}]
      const lessonsWithElements = chapterLessons.map(lesson => {
        return {
          ...lesson,
        };
      });
      
      return {
        ...chapter,
        lesson_list: lessonsWithElements
      };
    });
    
    // Build complete Quest object
    return {
      ...questData,
      chapters: chaptersWithLessons,
    };
  } catch (error) {
    console.error('Error building course data:', questId, error);
    throw error;
  }
}

export { getLatestUpdateTime };
